import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Product } from '../hooks/usePublishedProducts';
import { IoArrowBack, IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5';
import ChatButton from '../components/ChatButton';

export const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not found');
        setLoading(false);
        return;
      }

      try {
        // Fetch from published_products instead of inventory
        const productDoc = await getDoc(doc(db, 'published_products', productId));
        
        if (productDoc.exists()) {
          const productData = productDoc.data();
          let categoryName = 'Unknown';

          // Fetch category name from published_categories
          if (productData.categoryId) {
            try {
              const categoryDoc = await getDoc(doc(db, 'published_categories', productData.categoryId));
              if (categoryDoc.exists()) {
                categoryName = categoryDoc.data().name || 'Unknown';
              }
            } catch (err) {
              console.error('Error fetching category:', err);
            }
          }

          setProduct({ 
            id: productDoc.id, 
            ...productData,
            categoryName 
          } as Product);
          
          // Set first variant as default if product has variants
          if (productData.hasVariants && productData.variants && productData.variants.length > 0) {
            setSelectedVariant(productData.variants[0]);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-white to-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-white to-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Product not found'}</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-gray-600 hover:text-zinc-800 mb-6 transition-colors"
        >
          <IoArrowBack size={20} />
          Back to Products
        </button>

        {/* Product Detail */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Image */}
            <div className="bg-gradient-to-br from-zinc-100 to-gray-200 h-96 lg:h-full flex items-center justify-center">
              {/* Show variant image if variant is selected and has image, otherwise show product image */}
              {(selectedVariant?.image || (product.images && product.images.length > 0)) ? (
                <img
                  src={selectedVariant?.image || product.images[0]}
                  alt={selectedVariant?.name || product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-9xl">
                  {product.categoryName === 'CPU' && '🖥️'}
                  {product.categoryName === 'GPU' && '🎮'}
                  {product.categoryName === 'RAM' && '💾'}
                  {product.categoryName === 'Storage' && '💿'}
                  {product.categoryName === 'Motherboard' && '⚡'}
                  {product.categoryName === 'Case' && '📦'}
                  {product.categoryName === 'Power Supply' && '🔌'}
                  {product.categoryName === 'Cooling' && '❄️'}
                  {product.categoryName === 'Peripherals' && '⌨️'}
                  {product.categoryName === 'Monitor' && '🖥️'}
                  {product.categoryName === 'Pre-built PC' && '💻'}
                  {!['CPU', 'GPU', 'RAM', 'Storage', 'Motherboard', 'Case', 'Power Supply', 'Cooling', 'Peripherals', 'Monitor', 'Pre-built PC'].includes(product.categoryName || '') && '🔧'}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-8">
              {/* Category Badge */}
              <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full mb-4">
                {product.categoryName || 'Unknown'}
              </span>              {/* Product Name */}
              <h1 className="text-3xl lg:text-4xl font-bold text-zinc-800 mb-2">
                {product.name}
              </h1>

              {/* Brand */}
              <p className="text-xl text-gray-600 mb-6">{product.brand}</p>

              {/* Variants Selection */}
              {product.hasVariants && product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-zinc-800 mb-3">
                    Select Variant:
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedVariant?.id === variant.id
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex gap-3 items-start">
                          {/* Variant Image */}
                          {variant.image && (
                            <img
                              src={variant.image}
                              alt={variant.name}
                              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          
                          <div className="flex-1 flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-semibold text-zinc-800">{variant.name}</div>
                              {variant.sku && (
                                <div className="text-xs text-gray-500 mt-1">SKU: {variant.sku}</div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              {variant.priceType === 'range' && variant.priceRange ? (
                                <div className="font-bold text-zinc-800">
                                  ₱{variant.priceRange.min.toLocaleString()} - ₱{variant.priceRange.max.toLocaleString()}
                                </div>
                              ) : variant.price ? (
                                <div className="font-bold text-zinc-800">
                                  ₱{variant.price.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">Price on request</div>
                              )}
                              <div className="text-xs text-gray-600 mt-1">
                                {variant.stockType === 'order-basis' ? (
                                  <span className="text-blue-600">Order Basis</span>
                                ) : parseInt(variant.baseStock) > 0 ? (
                                  <span className="text-green-600">{variant.baseStock} in stock</span>
                                ) : (
                                  <span className="text-red-600">Out of stock</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Display */}
              {/* {!product.hasVariants && (
                <div className="mb-6">
                  {product.priceType === 'range' && product.priceRange ? (
                    <div className="text-3xl font-bold text-zinc-800">
                      ₱{product.priceRange.min.toLocaleString()} - ₱{product.priceRange.max.toLocaleString()}
                    </div>
                  ) : product.price ? (
                    <div className="text-3xl font-bold text-zinc-800">
                      ₱{product.price.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                    </div>
                  ) : (
                    <div className="text-xl font-medium text-gray-500">Price on request</div>
                  )}
                </div>
              )} */}

              {/* Stock Status */}
              <div className="mb-6">
                {product.hasVariants && selectedVariant ? (
                  selectedVariant.stockType === 'order-basis' ? (
                    <div className="flex items-center gap-2 text-blue-600">
                      <IoCheckmarkCircle size={24} />
                      <span className="text-lg font-semibold">Available to Order</span>
                    </div>
                  ) : parseInt(selectedVariant.baseStock) > 0 ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <IoCheckmarkCircle size={24} />
                      <span className="text-lg font-semibold">
                        In Stock ({selectedVariant.baseStock} available)
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-600">
                      <IoCloseCircle size={24} />
                      <span className="text-lg font-semibold">Out of Stock</span>
                    </div>
                  )
                ) : !product.hasVariants ? (
                  product.stockType === 'order-basis' ? (
                    <div className="flex items-center gap-2 text-blue-600">
                      <IoCheckmarkCircle size={24} />
                      <span className="text-lg font-semibold">Available to Order</span>
                    </div>
                  ) : (typeof product.baseStock === 'number' ? product.baseStock : parseInt(product.baseStock) || 0) > 0 ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <IoCheckmarkCircle size={24} />
                      <span className="text-lg font-semibold">
                        In Stock ({product.baseStock} available)
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-600">
                      <IoCloseCircle size={24} />
                      <span className="text-lg font-semibold">Out of Stock</span>
                    </div>
                  )
                ) : null}
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-zinc-800 mb-3">Description</h2>
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
              </div>

              {/* Contact CTA */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-zinc-800 mb-2">
                  Interested in this product?
                </h3>
                <p className="text-gray-600 mb-4">
                  Contact us for availability, inquiries, or to visit our branch locations.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/about#contact"
                    className="flex-1 px-6 py-3 bg-zinc-800 text-white text-center rounded-lg hover:bg-zinc-700 transition-colors font-medium"
                  >
                    Contact Us
                  </a>
                  <a
                    href="/about#branches"
                    className="flex-1 px-6 py-3 bg-white text-zinc-800 text-center rounded-lg border-2 border-zinc-800 hover:bg-zinc-50 transition-colors font-medium"
                  >
                    Visit Our Branches
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications */}
          {Object.keys(product.specifications).length > 0 && (
            <div className="border-t border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-zinc-800 mb-6">Specifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-semibold text-gray-700 mb-1">{key}</div>
                    <div className="text-gray-900">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-800 mb-4">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            Our expert team is ready to assist you with product recommendations, compatibility checks, 
            and any questions you may have about this product.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium text-zinc-800">📧 Email:</span>
              <a href="mailto:kissajasper@gmail.com" className="text-blue-600 hover:underline">
                kissajasper@gmail.com
              </a>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-zinc-800">📱 Phone:</span>
              <a href="tel:+639171475856" className="text-blue-600 hover:underline">
                +63 917 147 5856
              </a>
            </div>
          </div>
        </div>
      </div>
      <ChatButton 
        productId={product.id} 
        productName={product.name}
        productImage={product.images?.[0]}
      />
    </div>
  );
};
